<template>
  <div class="multiselect-main">
    <div v-if="value.length > 0" class="tag-chip-list">
      <div
        v-for="(selectedValue, index) in value"
        :key="`${selectedValue}-${index}`"
        class="tag-chip"
        :title="getSelectedOptionTitle(selectedValue)"
      >
        <div>{{ getSelectedOptionLabel(selectedValue) }}</div>
        <button type="button" @click="removeItem(selectedValue)" class="tag-chip-delete" title="Remove item">
          <img src="https://dsmlib.logistics.systems/Icons/icons8-minus-48.png" alt="" height="15" />
        </button>
      </div>
    </div>
    <div class="multiselect-input">
      <Dropdown
        :items="availableItems"
        :itemKey="itemKey"
        :selectedObject.sync="selectedObject"
        @selected="addItem"
        :placeholder="placeholder"
      />
      <button type="button" class="query-btn" @click="openTable" title="Inspect options">
        <img src="https://dsmlib.logistics.systems/Icons/MagnifyingGlass.png" alt="" height="15" />
      </button>
    </div>

    <div v-if="showTable" ref="tablePopup" class="table-popup">
      <div class="content">
        <TableBox
          :tableData="items"
          :title="`Inspect ${placeholder} options`"
          :selectedRow.sync="tableSelectedRows"
          :maxSelection="-1"
          :showSelectAll="true"
          :fitContentWidth="true"
        />
        <div class="button-container">
          <button type="button" @click="showTable = false">Cancel</button>
          <button type="button" @click="confirmTable">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data: function () {
    return {
      selectedObject: null,
      showTable: false,
      tableSelectedRows: [],
      teleportedTable: null,
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    itemKey: {
      type: String,
      default: 'label',
    },
    saveKey: {
      type: String,
      default: 'key',
    },
    placeholder: {
      type: String,
      default: 'Add...',
    },
    itemDesc: {
      type: String,
      default: '',
    },
  },
  computed: {
    availableItems() {
      return this.items.filter(
        (item) => !this.value.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), item[this.saveKey])),
      );
    },
  },
  methods: {
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    optionValueMatches(optionValue, savedValue) {
      if (!this.hasValue(optionValue) || !this.hasValue(savedValue)) return optionValue === savedValue;
      return String(optionValue) === String(savedValue);
    },
    getSelectedValue(item) {
      return item && typeof item == 'object' ? item[this.saveKey] : item;
    },
    normalizeSelectedItem(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.saveKey], item)) || {
        [this.saveKey]: item,
        [this.itemKey]: item,
      };
    },
    getSelectedOption(item) {
      if (item && typeof item == 'object') return item;
      return this.items.find((option) => this.optionValueMatches(option[this.saveKey], item));
    },
    getSelectedOptionLabel(item) {
      if (item && typeof item == 'object') return item[this.itemKey] ?? item[this.saveKey] ?? '';
      const option = this.getSelectedOption(item);
      return option?.[this.itemKey] ?? item;
    },
    getSelectedOptionTitle(item) {
      const option = this.getSelectedOption(item);
      if (!option) return this.getSelectedOptionLabel(item);

      const details = [];
      if (this.itemDesc && this.hasValue(option[this.itemDesc])) {
        details.push(`${this.itemDesc}: ${option[this.itemDesc]}`);
      }
      Object.keys(option).forEach((key) => {
        if (['label', 'key', 'value', this.itemDesc].includes(key) || !this.hasValue(option[key])) return;
        details.push(`${key}: ${option[key]}`);
      });
      return [this.getSelectedOptionLabel(option), ...details].join('\n');
    },
    addItem(item) {
      if (!item) return;
      const value = item[this.saveKey];
      if (this.value.some((selectedItem) => this.optionValueMatches(this.getSelectedValue(selectedItem), value))) return;
      this.$emit('update:value', [...this.value.map((selectedItem) => this.normalizeSelectedItem(selectedItem)), item]);
      this.$nextTick(() => {
        this.selectedObject = null;
      });
    },
    removeItem(item) {
      const value = this.getSelectedValue(item);
      this.$emit(
        'update:value',
        this.value
          .map((selectedItem) => this.normalizeSelectedItem(selectedItem))
          .filter((selectedItem) => !this.optionValueMatches(this.getSelectedValue(selectedItem), value)),
      );
    },
    openTable() {
      this.tableSelectedRows = this.value.map((selectedItem) => {
        const value = this.getSelectedValue(selectedItem);
        return this.items.find((item) => this.optionValueMatches(item[this.saveKey], value)) || this.normalizeSelectedItem(selectedItem);
      });
      this.showTable = true;
    },
    confirmTable() {
      const selectedRows = this.tableSelectedRows.map((item) => this.normalizeSelectedItem(item));
      this.showTable = false;
      this.$nextTick(() => {
        this.$emit('update:value', selectedRows);
      });
    },
    attachTableTeleport() {
      const table = this.$refs.tablePopup;
      if (!table || this.teleportedTable) return;
      table.style.position = 'fixed';
      table.style.zIndex = '99990';
      this.$getOverlayRoot().appendChild(table);
      this.teleportedTable = table;
    },
    detachTableTeleport() {
      if (this.teleportedTable && this.teleportedTable.parentNode) {
        this.teleportedTable.parentNode.removeChild(this.teleportedTable);
      }
      this.teleportedTable = null;
    },
    escapeCloseTable(event) {
      if (event.code == 'Escape') this.showTable = false;
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.escapeCloseTable);
    this.detachTableTeleport();
  },
  watch: {
    showTable(value) {
      if (value) {
        document.addEventListener('keydown', this.escapeCloseTable);
        this.$nextTick(() => this.attachTableTeleport());
      } else {
        document.removeEventListener('keydown', this.escapeCloseTable);
        this.detachTableTeleport();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.multiselect-main {
  width: 100%;

  .multiselect-input {
    display: flex;
    align-items: center;

    > :first-child {
      flex: 1;
    }

    .query-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 19px;
      height: 19px;
      padding: 0;
      margin-left: 0.25rem;
      border: none;
      background: transparent;
      cursor: pointer;
    }
  }

  .tag-chip-list {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin: 0.25rem 0 0.65rem;

    .tag-chip {
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      border: 1px solid lightgray;
      background-color: #f5f9ff;
      display: flex;
      align-items: center;
      position: relative;

      .tag-chip-delete {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 50%;
        right: 2px;
        width: 15px;
        height: 15px;
        cursor: pointer;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: linear-gradient(to left, rgba(245, 249, 255, 0.96) 0%, rgba(245, 249, 255, 0) 100%);
        transform: translateY(-50%);
      }

      @media (hover: hover) {
        .tag-chip-delete {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease;
        }

        &:hover .tag-chip-delete,
        &:focus-within .tag-chip-delete {
          opacity: 1;
          pointer-events: auto;
        }
      }
    }
  }

}

.table-popup {
  position: fixed;
  z-index: 99990;
  font-size: 12px;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: calc(#{$top-nav-height} + 10px);
  background-color: rgba(0, 0, 0, 0.3);

  .content {
    position: relative;
    width: fit-content;
    max-width: 70vw;
    height: 70%;
    padding: 10px;
    box-sizing: border-box;
    border-radius: 5px;
    background-color: white;
  }

  .button-container {
    position: absolute;
    top: 100%;
    right: 10px;
    margin: 0 3px;

    > button {
      background-color: white;
      border-radius: 0;
      border-bottom-right-radius: 5px;
      border-bottom-left-radius: 5px;

      &:not(:last-child) {
        margin-right: 5px;
      }

      &:last-child {
        margin-left: 20px;
      }
    }
  }
}
</style>
